// Pure geometry of the IMEXHS measurements (docs/measurement-constructions.md): handle layouts,
// drag constraints and the formulas behind every stored result. No DOM and no cornerstone here, so
// the same code serves the tools, the wire-format converter and plain-node tests.

export interface Point {
  x: number;
  y: number;
}

export interface Spacing {
  row: number;
  column: number;
  /** True when the image carries a real pixel spacing (lengths in mm, not pixels). */
  calibrated: boolean;
}

/** Handles of one measurement by name (the tool's Handle objects are mutated in place). */
export type HandleMap = Record<string, Point>;

export const UNCALIBRATED: Spacing = { row: 1, column: 1, calibrated: false };

/** Spacing from DICOM row/column spacing; uncalibrated unless both are positive. */
export function spacingFrom(row: number | undefined, column: number | undefined): Spacing {
  return row && column && row > 0 && column > 0 ? { row, column, calibrated: true } : UNCALIBRATED;
}

// --- vectors ----------------------------------------------------------------------------------------

export const add = (a: Point, b: Point): Point => ({ x: a.x + b.x, y: a.y + b.y });
export const sub = (a: Point, b: Point): Point => ({ x: a.x - b.x, y: a.y - b.y });
export const scale = (a: Point, k: number): Point => ({ x: a.x * k, y: a.y * k });
export const dot = (a: Point, b: Point): number => a.x * b.x + a.y * b.y;
export const norm = (v: Point): number => Math.hypot(v.x, v.y);
export const distance = (a: Point, b: Point): number => Math.hypot(b.x - a.x, b.y - a.y);
export const midpoint = (a: Point, b: Point): Point => ({ x: (a.x + b.x) / 2, y: (a.y + b.y) / 2 });

/** Image-pixel vector a→b scaled to millimetres (or left in pixels when uncalibrated). */
export function physicalVector(a: Point, b: Point, spacing: Spacing): Point {
  return { x: (b.x - a.x) * spacing.column, y: (b.y - a.y) * spacing.row };
}

export function physicalLength(a: Point, b: Point, spacing: Spacing): number {
  return norm(physicalVector(a, b, spacing));
}

const DEGREES_PER_RADIAN = 180 / Math.PI;

/** Acute angle (0–90°) between two directions. */
export function acuteAngle(u: Point, v: Point): number {
  const denominator = norm(u) * norm(v);
  if (denominator === 0) return 0;
  return Math.acos(Math.min(1, Math.abs(dot(u, v) / denominator))) * DEGREES_PER_RADIAN;
}

/** Angle (0–180°) at `vertex` between the rays to `a` and `b`, in physical space. */
export function angleAt(vertex: Point, a: Point, b: Point, spacing: Spacing): number | undefined {
  const u = physicalVector(vertex, a, spacing);
  const v = physicalVector(vertex, b, spacing);
  const denominator = norm(u) * norm(v);
  if (denominator === 0) return undefined;
  return Math.acos(Math.max(-1, Math.min(1, dot(u, v) / denominator))) * DEGREES_PER_RADIAN;
}

/** Intersection of the infinite lines a1a2 and b1b2 (undefined when parallel or degenerate). */
export function lineIntersection(a1: Point, a2: Point, b1: Point, b2: Point): Point | undefined {
  const r = sub(a2, a1);
  const s = sub(b2, b1);
  const denominator = r.x * s.y - r.y * s.x;
  if (Math.abs(denominator) < 1e-12) return undefined;
  const t = ((b1.x - a1.x) * s.y - (b1.y - a1.y) * s.x) / denominator;
  return { x: a1.x + r.x * t, y: a1.y + r.y * t };
}

/** Rounds like the viewer being replaced (`toFixed` semantics), returning a number. */
export function round(value: number, decimals: number): number {
  return Number(value.toFixed(decimals));
}

// --- axis frame -------------------------------------------------------------------------------------

/** A point in the frame of an axis: distance from `start` along it, signed distance along its normal. */
export interface AxisCoords {
  along: number;
  offset: number;
}

interface Axis {
  start: Point;
  dir: Point;
  normal: Point;
  length: number;
}

/** Axis start→end; normal = (dir.y, −dir.x), the side IMEXHS calls "perpendicularEnd". */
function axisOf(start: Point, end: Point): Axis | undefined {
  const length = distance(start, end);
  if (length === 0) return undefined;
  const dir = scale(sub(end, start), 1 / length);
  return { start, dir, normal: { x: dir.y, y: -dir.x }, length };
}

export function toAxis(p: Point, start: Point, end: Point): AxisCoords {
  const axis = axisOf(start, end);
  if (!axis) return { along: 0, offset: 0 };
  const d = sub(p, start);
  return { along: dot(d, axis.dir), offset: dot(d, axis.normal) };
}

export function fromAxis(c: AxisCoords, start: Point, end: Point): Point {
  const axis = axisOf(start, end);
  if (!axis) return { x: start.x, y: start.y };
  return add(start, add(scale(axis.dir, c.along), scale(axis.normal, c.offset)));
}

function place(target: Point, p: Point): void {
  target.x = p.x;
  target.y = p.y;
}

const copyPoint = (p: Point): Point => ({ x: p.x, y: p.y });

export function snapshotHandles(handles: HandleMap, keys: readonly string[]): HandleMap {
  const out: HandleMap = {};
  for (const key of keys) if (handles[key]) out[key] = copyPoint(handles[key]);
  return out;
}

// --- constructions ------------------------------------------------------------------------------------

/** A bar perpendicular to the axis: `neg` handle on the −normal side, `pos` handle on the +normal side. */
interface Bar {
  neg: string;
  pos: string;
}

/** Feet stay this many pixels inside the axis (as IMEXHS does). */
const FOOT_MARGIN_PX = 1;
/** Minimum spacing (pixels) between ordered feet/intersections along the coxometry axis. */
const ORDER_MARGIN_PX = 3;
/** Shortest axis a drag may leave. */
const MIN_AXIS_PX = 1;

/** How the dependents follow when an axis end moves. */
type AxisFollow = 'fixed-end' | 'ratio';

export interface Construction {
  /** Handle names in IMEXHS wire order. */
  order: readonly string[];
  /** Drags needed to create it (each drag = one segment). */
  segments: 1 | 2;
  /** Recomputes the dependent handles from the axis while the measurement is locked. */
  layout?(h: HandleMap, spacing: Spacing): void;
  /**
   * Adjusts the geometry after the single handle `moved` was dragged from `previous`.
   * Returns false to reject the move (the caller restores `previous`).
   */
  constrain?(h: HandleMap, moved: string, previous: HandleMap): boolean;
}

/** Moves every dependent handle with the axis (start/end dragged). */
function followAxis(h: HandleMap, previous: HandleMap, moved: 'start' | 'end', mode: AxisFollow, keys: readonly string[], feetMustStayInside: boolean): boolean {
  const oldLength = distance(previous.start, previous.end);
  const newLength = distance(h.start, h.end);
  if (newLength < MIN_AXIS_PX || oldLength === 0) return false;
  const placed: HandleMap = {};
  for (const key of keys) {
    if (key === 'start' || key === 'end' || !previous[key]) continue;
    const c = toAxis(previous[key], previous.start, previous.end);
    let along: number;
    if (mode === 'ratio') along = (c.along / oldLength) * newLength;
    else if (moved === 'start') along = newLength - (oldLength - c.along);
    else along = c.along;
    if (feetMustStayInside && (along < 0 || along > newLength)) return false;
    placed[key] = fromAxis({ along, offset: c.offset }, h.start, h.end);
  }
  for (const [key, p] of Object.entries(placed)) place(h[key], p);
  return true;
}

function clampFoot(along: number, length: number): number {
  return Math.min(length - FOOT_MARGIN_PX, Math.max(FOOT_MARGIN_PX, along));
}

/**
 * A bar handle dragged: the bar stays perpendicular, its foot is the pointer's projection (kept inside
 * the axis unless `fixedAlong` pins it), the dragged handle takes the pointer's distance on its own side
 * and the partner keeps its offset.
 */
function moveBarHandle(h: HandleMap, previous: HandleMap, bar: Bar, moved: string, fixedAlong?: number): boolean {
  const length = distance(h.start, h.end);
  if (length < 2 * FOOT_MARGIN_PX + MIN_AXIS_PX) return false;
  const pointer = toAxis(h[moved], h.start, h.end);
  const along = fixedAlong ?? clampFoot(pointer.along, length);
  const partner = moved === bar.neg ? bar.pos : bar.neg;
  const side = moved === bar.neg ? -1 : 1;
  const partnerOffset = toAxis(previous[partner], h.start, h.end).offset;
  place(h[moved], fromAxis({ along, offset: side * Math.abs(pointer.offset) }, h.start, h.end));
  place(h[partner], fromAxis({ along, offset: partnerOffset }, h.start, h.end));
  return true;
}

function barFor(bars: readonly Bar[], key: string): Bar | undefined {
  return bars.find((bar) => bar.neg === key || bar.pos === key);
}

// Bidirectional -------------------------------------------------------------------------------------

const BIDIRECTIONAL_BAR: Bar = { neg: 'perpendicularStart', pos: 'perpendicularEnd' };

export const BIDIRECTIONAL: Construction = {
  order: ['start', 'end', 'perpendicularStart', 'perpendicularEnd'],
  segments: 1,
  layout(h, spacing) {
    const { start, end } = h;
    const arm = physicalLength(start, end, spacing) / 4;
    const middle = midpoint(start, end);
    const n = axisOf(start, end)?.normal ?? { x: 0, y: 0 };
    place(h.perpendicularStart, sub(middle, scale(n, arm)));
    place(h.perpendicularEnd, add(middle, scale(n, arm)));
  },
  constrain(h, moved, previous) {
    if (moved === 'start' || moved === 'end') return followAxis(h, previous, moved, 'fixed-end', this.order, true);
    return moveBarHandle(h, previous, BIDIRECTIONAL_BAR, moved);
  },
};

export interface BidirectionalValues {
  length: number;
  width: number;
}

/** Longest and shortest of the two diameters (IMEXHS swaps them when the bar is the longer one). */
export function measureBidirectional(h: HandleMap, spacing: Spacing): BidirectionalValues {
  const axis = physicalLength(h.start, h.end, spacing);
  const bar = physicalLength(h.perpendicularStart, h.perpendicularEnd, spacing);
  return { length: Math.max(axis, bar), width: Math.min(axis, bar) };
}

// Parallel distance --------------------------------------------------------------------------------------

const PARALLEL_BARS: readonly Bar[] = [
  { neg: 'perpendicularStart', pos: 'perpendicularEnd' },
  { neg: 'perpendicularStart2', pos: 'perpendicularEnd2' },
];

export const PARALLEL_DISTANCE: Construction = {
  order: ['start', 'end', 'perpendicularStart', 'perpendicularStart2', 'perpendicularEnd', 'perpendicularEnd2'],
  segments: 1,
  layout(h) {
    const { start, end } = h;
    const length = distance(start, end);
    const n = axisOf(start, end)?.normal ?? { x: 0, y: 0 };
    const d = sub(end, start);
    const foot1 = add(start, scale(d, 1 / 4));
    const foot2 = add(start, scale(d, 3 / 4));
    place(h.perpendicularStart, sub(foot1, scale(n, length / 8)));
    place(h.perpendicularEnd, add(foot1, scale(n, length / 2)));
    place(h.perpendicularStart2, sub(foot2, scale(n, length / 8)));
    place(h.perpendicularEnd2, add(foot2, scale(n, length / 2)));
  },
  constrain(h, moved, previous) {
    if (moved === 'start' || moved === 'end') return followAxis(h, previous, moved, 'fixed-end', this.order, true);
    const bar = barFor(PARALLEL_BARS, moved);
    return bar ? moveBarHandle(h, previous, bar, moved) : false;
  },
};

/** Where bar `bar` crosses the axis line. */
function footOf(h: HandleMap, bar: Bar): Point | undefined {
  return lineIntersection(h.start, h.end, h[bar.neg], h[bar.pos]);
}

/** Distance between the two bars' feet on the axis. */
export function measureParallelDistance(h: HandleMap, spacing: Spacing): number | undefined {
  const foot1 = footOf(h, PARALLEL_BARS[0]);
  const foot2 = footOf(h, PARALLEL_BARS[1]);
  return foot1 && foot2 ? physicalLength(foot1, foot2, spacing) : undefined;
}

export function parallelFeet(h: HandleMap): Point[] {
  return PARALLEL_BARS.map((bar) => footOf(h, bar)).filter((p): p is Point => p !== undefined);
}

// Cardiothoracic index ----------------------------------------------------------------------------------

const THORACIC_BAR: Bar = { neg: 'perpendicularStart', pos: 'perpendicularEnd' };
/** Half-width bars of the heart: the free handle and its foot on the midline. */
const CARDIAC_HALVES = [
  { free: 'leftStart', foot: 'leftEnd', side: -1 },
  { free: 'rightStart', foot: 'rightEnd', side: 1 },
] as const;

export const CARDIOTHORACIC_INDEX: Construction = {
  order: ['start', 'end', 'perpendicularStart', 'leftEnd', 'leftStart', 'perpendicularEnd', 'rightEnd', 'rightStart'],
  segments: 1,
  layout(h, spacing) {
    const { start, end } = h;
    const arm = physicalLength(start, end, spacing) / 4;
    const n = axisOf(start, end)?.normal ?? { x: 0, y: 0 };
    const middle = midpoint(start, end);
    place(h.perpendicularStart, sub(end, scale(n, arm)));
    place(h.perpendicularEnd, add(end, scale(n, arm)));
    place(h.leftStart, sub(middle, scale(n, arm)));
    place(h.leftEnd, middle);
    place(h.rightStart, add(middle, scale(n, arm)));
    place(h.rightEnd, middle);
  },
  constrain(h, moved, previous) {
    if (moved === 'start' || moved === 'end') return followAxis(h, previous, moved, 'ratio', this.order, false);
    const length = distance(h.start, h.end);
    if (moved === THORACIC_BAR.neg || moved === THORACIC_BAR.pos) return moveBarHandle(h, previous, THORACIC_BAR, moved, length);
    for (const half of CARDIAC_HALVES) {
      if (moved === half.free) {
        const pointer = toAxis(h[moved], h.start, h.end);
        if (Math.sign(pointer.offset) !== half.side || length < 2 * FOOT_MARGIN_PX + MIN_AXIS_PX) return false;
        const along = clampFoot(pointer.along, length);
        place(h[half.free], fromAxis({ along, offset: pointer.offset }, h.start, h.end));
        place(h[half.foot], fromAxis({ along, offset: 0 }, h.start, h.end));
        return true;
      }
      if (moved === half.foot) {
        if (length < 2 * FOOT_MARGIN_PX + MIN_AXIS_PX) return false;
        const along = clampFoot(toAxis(h[moved], h.start, h.end).along, length);
        const offset = toAxis(previous[half.free], h.start, h.end).offset;
        place(h[half.free], fromAxis({ along, offset }, h.start, h.end));
        place(h[half.foot], fromAxis({ along, offset: 0 }, h.start, h.end));
        return true;
      }
    }
    return false;
  },
};

export interface CardiothoracicValues {
  A: number;
  B: number;
  C: number;
  /** 100·(A + B)/C, from A, B, C rounded to 3 decimals (as IMEXHS computes it). */
  index: number | undefined;
}

export function measureCardiothoracic(h: HandleMap, spacing: Spacing): CardiothoracicValues {
  const A = physicalLength(h.leftStart, h.leftEnd, spacing);
  const B = physicalLength(h.rightStart, h.rightEnd, spacing);
  const C = physicalLength(h.perpendicularStart, h.perpendicularEnd, spacing);
  const roundedC = round(C, 3);
  const index = roundedC === 0 ? undefined : (100 * (round(A, 3) + round(B, 3))) / roundedC;
  return { A, B, C, index };
}

// Coxometry -------------------------------------------------------------------------------------------------

const PERKIN_BARS: readonly Bar[] = [
  { neg: 'perpendicularStart', pos: 'perpendicularEnd' },
  { neg: 'perpendicularStart2', pos: 'perpendicularEnd2' },
];
/** Roof line i runs angleStart(i) → angleEnd(i); angleEnd(i) always sits on perpendicularEnd(i). */
const ROOF_LINES = [
  { start: 'angleStart', end: 'angleEnd', perkinEnd: 'perpendicularEnd' },
  { start: 'angleStart2', end: 'angleEnd2', perkinEnd: 'perpendicularEnd2' },
] as const;
/** Perkin feet sit this many pixels inside the ends of Hilgenreiner's line when created. */
const PERKIN_INSET_PX = 10;

function pinRoofEnds(h: HandleMap): void {
  for (const roof of ROOF_LINES) place(h[roof.end], h[roof.perkinEnd]);
}

interface CoxometryPoints {
  f1: Point;
  f2: Point;
  a1: Point;
  a2: Point;
}

/** Perkin feet (f) and roof-line crossings (a) on Hilgenreiner's line. */
function coxometryPoints(h: HandleMap): CoxometryPoints | undefined {
  const f1 = footOf(h, PERKIN_BARS[0]);
  const f2 = footOf(h, PERKIN_BARS[1]);
  const a1 = lineIntersection(h.start, h.end, h.angleStart, h.angleEnd);
  const a2 = lineIntersection(h.start, h.end, h.angleStart2, h.angleEnd2);
  return f1 && f2 && a1 && a2 ? { f1, f2, a1, a2 } : undefined;
}

/** f1 < a1, a2 < f2 and f1 < f2 along the axis, with a small margin. */
function coxometryOrdered(h: HandleMap): boolean {
  const points = coxometryPoints(h);
  if (!points) return true;
  const along = (p: Point) => toAxis(p, h.start, h.end).along;
  const [f1, f2, a1, a2] = [along(points.f1), along(points.f2), along(points.a1), along(points.a2)];
  return f1 + ORDER_MARGIN_PX < a1 && a2 + ORDER_MARGIN_PX < f2 && f1 + ORDER_MARGIN_PX < f2;
}

export const COXOMETRY: Construction = {
  order: [
    'start', 'end', 'perpendicularStart', 'perpendicularStart2', 'perpendicularEnd', 'perpendicularEnd2',
    'angleStart', 'angleStart2', 'angleEnd', 'angleEnd2',
  ],
  segments: 1,
  layout(h) {
    const { start, end } = h;
    const axis = axisOf(start, end);
    const length = axis?.length ?? 0;
    const dir = axis?.dir ?? { x: 0, y: 0 };
    const n = axis?.normal ?? { x: 0, y: 0 };
    const foot1 = add(start, scale(dir, PERKIN_INSET_PX));
    const foot2 = sub(end, scale(dir, PERKIN_INSET_PX));
    place(h.perpendicularStart, sub(foot1, scale(n, length / 4)));
    place(h.perpendicularEnd, add(foot1, scale(n, length / 4)));
    place(h.perpendicularStart2, sub(foot2, scale(n, length / 4)));
    place(h.perpendicularEnd2, add(foot2, scale(n, length / 4)));
    pinRoofEnds(h);
    const feetGap = distance(foot1, foot2);
    const bar1 = distance(h.perpendicularStart, h.perpendicularEnd);
    const bar2 = distance(h.perpendicularStart2, h.perpendicularEnd2);
    place(h.angleStart, sub(add(foot1, scale(dir, feetGap / 4)), scale(n, bar1 / 6)));
    place(h.angleStart2, sub(sub(foot2, scale(dir, feetGap / 4)), scale(n, bar2 / 4)));
  },
  constrain(h, moved, previous) {
    if (moved === 'start' || moved === 'end') {
      const ok = followAxis(h, previous, moved, 'fixed-end', this.order, false);
      pinRoofEnds(h);
      return ok;
    }
    let key = moved;
    const roofByEnd = ROOF_LINES.find((roof) => roof.end === moved);
    if (roofByEnd) {
      place(h[roofByEnd.perkinEnd], h[moved]);
      key = roofByEnd.perkinEnd;
    }
    const bar = barFor(PERKIN_BARS, key);
    if (bar) {
      if (!moveBarHandle(h, previous, bar, key)) return false;
    } else if (ROOF_LINES.some((roof) => roof.start === key)) {
      if (toAxis(h[key], h.start, h.end).offset >= 0) return false;
    } else {
      return false;
    }
    pinRoofEnds(h);
    return coxometryOrdered(h);
  },
};

export interface CoxometryValues {
  D: number;
  I: number;
  D1: number;
  D2: number;
  H1: number;
  H2: number;
}

export function measureCoxometry(h: HandleMap, spacing: Spacing): CoxometryValues | undefined {
  const points = coxometryPoints(h);
  if (!points) return undefined;
  const { f1, f2, a1, a2 } = points;
  const D = angleAt(a1, h.angleEnd, f1, spacing);
  const I = angleAt(a2, h.angleEnd2, f2, spacing);
  if (D === undefined || I === undefined) return undefined;
  return {
    D,
    I,
    D1: physicalLength(a1, f1, spacing),
    D2: physicalLength(f2, a2, spacing),
    H1: physicalLength(h.perpendicularStart, f1, spacing),
    H2: physicalLength(h.perpendicularStart2, f2, spacing),
  };
}

/** Canvas anchor points of the coxometry labels (image coordinates). */
export function coxometryLabelPoints(h: HandleMap): Partial<Record<keyof CoxometryValues, Point>> {
  const points = coxometryPoints(h);
  if (!points) return {};
  const { f1, f2, a1, a2 } = points;
  return {
    H1: midpoint(h.perpendicularStart, f1),
    H2: midpoint(h.perpendicularStart2, f2),
    D1: midpoint(a1, f1),
    D2: midpoint(a2, f2),
    D: a1,
    I: a2,
  };
}

// Two-line tools ---------------------------------------------------------------------------------------------

export const TWO_LINES: Construction = {
  order: ['start', 'end', 'start2', 'end2'],
  segments: 2,
};

export interface InsallSalvatiValues {
  A: number;
  B: number;
  ratioAB: number | undefined;
  ratioBA: number | undefined;
}

export function measureInsallSalvati(h: HandleMap, spacing: Spacing): InsallSalvatiValues {
  const A = physicalLength(h.start, h.end, spacing);
  const B = physicalLength(h.start2, h.end2, spacing);
  return { A, B, ratioAB: B === 0 ? undefined : A / B, ratioBA: A === 0 ? undefined : B / A };
}

/** Acute angle between line 1 and line 2 (femoral anteversion). */
export function measureTwoLineAngle(h: HandleMap, spacing: Spacing): number {
  return acuteAngle(physicalVector(h.start, h.end, spacing), physicalVector(h.start2, h.end2, spacing));
}

/** Cobb angle as the beta tool computes it: handle coordinates rounded up first. */
export function measureCobb(h: HandleMap, spacing: Spacing): number {
  const up = (p: Point): Point => ({ x: Math.ceil(p.x), y: Math.ceil(p.y) });
  return acuteAngle(physicalVector(up(h.start), up(h.end), spacing), physicalVector(up(h.start2), up(h.end2), spacing));
}

// Built-in shapes -------------------------------------------------------------------------------------------

export function measureLength(h: HandleMap, spacing: Spacing): number {
  return physicalLength(h.start, h.end, spacing);
}

export function measureAngle(h: HandleMap, spacing: Spacing): number | undefined {
  return angleAt(h.middle, h.start, h.end, spacing);
}

export function rectangleArea(a: Point, b: Point, spacing: Spacing): number {
  return Math.abs(b.x - a.x) * spacing.column * Math.abs(b.y - a.y) * spacing.row;
}

export function ellipseArea(a: Point, b: Point, spacing: Spacing): number {
  return Math.PI * ((Math.abs(b.x - a.x) * spacing.column) / 2) * ((Math.abs(b.y - a.y) * spacing.row) / 2);
}

/** Shoelace area of a closed polygon. */
export function polygonArea(vertices: readonly Point[], spacing: Spacing): number {
  let twice = 0;
  for (let i = 0; i < vertices.length; i++) {
    const a = vertices[i];
    const b = vertices[(i + 1) % vertices.length];
    twice += a.x * b.y - b.x * a.y;
  }
  return (Math.abs(twice) / 2) * spacing.column * spacing.row;
}

/** Constructions of the tools implemented in src/tools/segments.ts, by cornerstone tool name. */
export const CONSTRUCTIONS: Record<string, Construction> = {
  Bidirectional: BIDIRECTIONAL,
  ParallelDistance: PARALLEL_DISTANCE,
  CardiothoracicIndex: CARDIOTHORACIC_INDEX,
  Coxometry: COXOMETRY,
  InsallSalvati: TWO_LINES,
  FemoralAnteversion: TWO_LINES,
};

/**
 * Applies one observed change of a measurement's handles: when exactly one handle moved since
 * `previous`, lays the measurement out again (locked) or constrains it, restoring the moved handle
 * when the construction rejects the move. Returns true when the measurement is still locked.
 */
export function applyHandleChange(construction: Construction, h: HandleMap, previous: HandleMap, locked: boolean, spacing: Spacing): boolean {
  const moved = construction.order.filter((key) => h[key] && previous[key] && (h[key].x !== previous[key].x || h[key].y !== previous[key].y));
  if (moved.length !== 1) return locked;
  const key = moved[0];
  const axisMoved = key === 'start' || key === 'end';
  if (locked && axisMoved && construction.layout) {
    construction.layout(h, spacing);
    return true;
  }
  if (!construction.constrain) return locked;
  const accepted = construction.constrain(h, key, previous);
  if (!accepted) {
    for (const k of construction.order) if (h[k] && previous[k]) place(h[k], previous[k]);
    return locked;
  }
  return locked && axisMoved;
}
